import React, { useState } from 'react';
import { Plus, X, Filter } from 'lucide-react';
import {
  getOperatorsForFieldType,
  getOperatorLabel,
  type FilterCondition,
  type FilterOperator
} from '../../../services/queries/listViewQueries';
import { StandardButton, StandardSelect, StandardInput } from '../../standard';

interface FilterBuilderProps {
  filters: FilterCondition[];
  availableFields: any[];
  onChange: (filters: FilterCondition[]) => void;
}

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  filters,
  availableFields,
  onChange
}) => {
  const [editingFilter, setEditingFilter] = useState<string | null>(null);

  const handleAddFilter = () => {
    const firstField = availableFields[0];
    if (!firstField) return;

    const newFilter: FilterCondition = {
      id: `filter_${Date.now()}`,
      fieldId: firstField.id,
      fieldLabel: firstField.fieldLabel,
      fieldType: firstField.fieldType,
      operator: 'equals',
      value: ''
    };

    onChange([...filters, newFilter]);
    setEditingFilter(newFilter.id);
  };

  const handleUpdateFilter = (filterId: string, updates: Partial<FilterCondition>) => {
    onChange(filters.map(f => {
      if (f.id === filterId) {
        // If field changed, reset operator and value
        if (updates.fieldId) {
          const field = availableFields.find(af => af.id === updates.fieldId);
          if (field) {
            return {
              ...f,
              fieldId: field.id,
              fieldLabel: field.fieldLabel,
              fieldType: field.fieldType,
              operator: 'equals',
              value: ''
            };
          }
        }
        return { ...f, ...updates };
      }
      return f;
    }));
  };

  const handleRemoveFilter = (filterId: string) => {
    onChange(filters.filter(f => f.id !== filterId));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Filters ({filters.length})
          </h3>
        </div>
        <StandardButton
          size="sm"
          variant="primary"
          onClick={handleAddFilter}
          leftIcon={<Plus className="w-4 h-4" />}
        >
          Add Filter
        </StandardButton>
      </div>

      {filters.length > 0 ? (
        <div className="space-y-3">
          {filters.map((filter, index) => (
            <FilterRow
              key={filter.id}
              filter={filter}
              availableFields={availableFields}
              isEditing={editingFilter === filter.id}
              onEdit={() => setEditingFilter(filter.id)}
              onUpdate={(updates) => handleUpdateFilter(filter.id, updates)}
              onRemove={() => handleRemoveFilter(filter.id)}
              showAnd={index > 0}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-6 bg-gray-50 dark:bg-gray-900 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700">
          <Filter className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No filters added</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Click "Add Filter" to create your first filter condition
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================
// FILTER ROW COMPONENT
// ============================================

interface FilterRowProps {
  filter: FilterCondition;
  availableFields: any[];
  isEditing: boolean;
  onEdit: () => void;
  onUpdate: (updates: Partial<FilterCondition>) => void;
  onRemove: () => void;
  showAnd: boolean;
}

const FilterRow: React.FC<FilterRowProps> = ({
  filter,
  availableFields,
  isEditing,
  onEdit,
  onUpdate,
  onRemove,
  showAnd
}) => {
  const selectedField = availableFields.find(f => f.id === filter.fieldId);
  const availableOperators = selectedField ? getOperatorsForFieldType(selectedField.fieldType) : [];

  const needsValue = !['isNull', 'isNotNull'].includes(filter.operator);

  return (
    <div className="space-y-2">
      {showAnd && (
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-2">AND</span>
          <div className="h-px flex-1 bg-gray-200 dark:bg-gray-700" />
        </div>
      )}

      <div className="flex items-start gap-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        {isEditing ? (
          <>
            {/* Field Selector */}
            <select
              value={filter.fieldId}
              onChange={(e) => onUpdate({ fieldId: e.target.value })}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {availableFields.map(field => (
                <option key={field.id} value={field.id}>
                  {field.fieldLabel}
                </option>
              ))}
            </select>

            {/* Operator Selector */}
            <select
              value={filter.operator}
              onChange={(e) => onUpdate({ operator: e.target.value as FilterOperator })}
              className="w-48 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {availableOperators.map(op => (
                <option key={op} value={op}>
                  {getOperatorLabel(op)}
                </option>
              ))}
            </select>

            {/* Value Input */}
            {needsValue && (
              <input
                type={selectedField?.fieldType === 'number' || selectedField?.fieldType === 'currency' ? 'number' : 'text'}
                value={String(filter.value)}
                onChange={(e) => onUpdate({ value: e.target.value })}
                placeholder="Enter value..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            )}

            {/* Remove Button */}
            <button
              onClick={onRemove}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            {/* Display Mode */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900 dark:text-white">
                <span className="font-medium">{filter.fieldLabel}</span>
                {' '}
                <span className="text-gray-500 dark:text-gray-400">{getOperatorLabel(filter.operator).toLowerCase()}</span>
                {needsValue && (
                  <>
                    {' '}
                    <span className="font-medium text-purple-600 dark:text-purple-400">
                      {filter.value || '(empty)'}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Edit/Remove Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={onEdit}
                className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                title="Edit filter"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                onClick={onRemove}
                className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                title="Remove filter"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
